import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import Swal from 'sweetalert2';
import axios from 'axios';
import { 
    FaUser, FaMapMarkerAlt, FaGraduationCap, 
    FaUpload, FaSyncAlt, FaPaperPlane, 
    FaBookOpen, FaCalculator 
} from 'react-icons/fa';
import './AdmissionForm.css';

import logo from '../../assets/logo.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- CONSTANTS ---
const OPTIONAL_SUBJECTS = [
    "हिंदी साहित्य (Hindi Lit.)", "राजनीति विज्ञान (Pol. Science)", 
    "समाजशास्त्र (Sociology)", "इतिहास (History)", 
    "अर्थशास्त्र (Economics)", "भूगोल (Geography)", 
    "लोक प्रशासन (Public Admin)", "फिजिकल एजुकेशन (Physical Edu)", 
    "मिलिट्री साइंस (Military Science)", "अन्य (Other)"
];

const AdmissionForm = () => {
    // --- STATE ---
    const [formData, setFormData] = useState({
        // Session
        sessionStart: '', sessionEnd: '',
        
        // Personal
        studentName: '', fatherName: '', motherName: '', 
        dob: '', gender: '', bloodGroup: '', 
        aadharNumber: '', caste: '', religion: '', category: '',
        
        // Contact
        permanentAddress: '', currentAddress: '', sameAddress: false,
        mobile: '', email: '',
        
        // Education
        admissionClass: '', lastExamName: '', studentStatus: 'Regular', lastInstitution: '',
        
        // Subjects
        compulsorySubject: '', medium: '', optionalSubjects: [],
        
        // Result
        totalMarks: '', obtainedMarks: '', percentage: '',
        
        declaration: false
    });

    // Removed aadharDoc from state
    const [files, setFiles] = useState({ photo: null });
    const [previews, setPreviews] = useState({ photo: null });
    const [captcha, setCaptcha] = useState('');
    const [userCaptcha, setUserCaptcha] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isOtherReligion, setIsOtherReligion] = useState(false);

    const fileRefs = { photo: useRef(null) };

    // --- CAPTCHA & INIT ---
    const generateCaptcha = () => {
        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let code = '';
        for (let i = 0; i < 6; i++) { code += chars[Math.floor(Math.random() * chars.length)]; }
        setCaptcha(code);
    };

    useEffect(() => {
        generateCaptcha();
        return () => { Object.values(previews).forEach(url => { if(url) URL.revokeObjectURL(url) }); };
    }, []);

    // --- HANDLERS ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        const onlyLettersRegex = /^[A-Za-z\s]*$/;

        // Session Year Validation (Exactly 2 digits)
        if (name === 'sessionStart' || name === 'sessionEnd') {
            // Allow only digits
            const numericValue = value.replace(/\D/g, '');

            // Prevent more than 2 digits
            if (numericValue.length > 2) return;

            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));

            return;
        }

        if (
            name === 'studentName' ||
            name === 'fatherName' ||
            name === 'motherName' ||
            name === 'caste' ||
            name === 'religion'
        ) {
            if (!onlyLettersRegex.test(value)) {
                return; // Do not update state if invalid character
            }
        } 

        // Aadhaar Validation (Strictly 12 digits only)
        if (name === 'aadharNumber') {
            // Allow only digits
            const numericValue = value.replace(/\D/g, '');

            // Prevent more than 12 digits
            if (numericValue.length > 12) return;

            setFormData(prev => ({
                ...prev,
                aadharNumber: numericValue
            }));

            return;
        }

        // Handle Checkbox for Same Address
        if (name === 'sameAddress') {
            setFormData(prev => ({
                ...prev,
                sameAddress: checked,
                currentAddress: checked ? prev.permanentAddress : ''
            }));
            return;
        }

        if (name === 'mobile') {
            // Remove any non-digit characters
            const numericValue = value.replace(/\D/g, '');

            // Allow only if empty OR first digit is 6-9
            if (numericValue === '' || /^[6-9]\d{0,9}$/.test(numericValue)) {
                setFormData(prev => ({ ...prev, mobile: numericValue }));
            }
            return;
        }

        if (name === 'optionalSubjects') {
            let updatedSubjects = [...formData.optionalSubjects];

            if (checked) {
                if (updatedSubjects.length >= 3) {
                    Swal.fire(
                        'Limit Reached',
                        'आप केवल 3 ऐच्छिक विषय चुन सकते हैं (Only 3 subjects allowed).',
                        'warning'
                    );
                    return;
                }
                updatedSubjects.push(value);
            } else {
                updatedSubjects = updatedSubjects.filter(sub => sub !== value);
            }

            setFormData(prev => ({
                ...prev,
                optionalSubjects: updatedSubjects
            }));
            return;
        }

        // Handle Marks Calculation
        if (name === 'totalMarks' || name === 'obtainedMarks') {
            const total = name === 'totalMarks' ? parseFloat(value) : parseFloat(formData.totalMarks);
            const obtained = name === 'obtainedMarks' ? parseFloat(value) : parseFloat(formData.obtainedMarks);
            
            let percent = '';
            if (!isNaN(total) && !isNaN(obtained) && total > 0) {
                percent = ((obtained / total) * 100).toFixed(2);
            }
            
            setFormData(prev => ({ ...prev, [name]: value, percentage: percent }));
            return;
        }

        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleReligionDropdownChange = (e) => {
        const val = e.target.value;
        if (val === "Other") {
            setIsOtherReligion(true);
            setFormData(prev => ({ ...prev, religion: "" }));
        } else {
            setIsOtherReligion(false);
            setFormData(prev => ({ ...prev, religion: val }));
        }
    };

    const handleAddressBlur = () => {
        if (formData.sameAddress) {
            setFormData(prev => ({ ...prev, currentAddress: prev.permanentAddress }));
        }
    };

    const handleFileChange = (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;

        const sizeKB = file.size / 1024;
        
        // Updated Validation: Min 50KB, Max 200KB
        if (fieldName === 'photo') {
            if (sizeKB < 5 || sizeKB > 200) { 
                Swal.fire('Invalid Size', 'Photo must be between 5KB and 200KB.', 'warning');
                e.target.value = null; return;
            }
        }

        setFiles(prev => ({ ...prev, [fieldName]: file }));
        if(previews[fieldName]) URL.revokeObjectURL(previews[fieldName]);
        setPreviews(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedData = {};
        Object.keys(formData).forEach(key => {
            trimmedData[key] =
                typeof formData[key] === "string"
                    ? formData[key].trim()
                    : formData[key];
        });
        // Validations
        if (!/^[6-9][0-9]{9}$/.test(formData.mobile)) {
            return Swal.fire(
                'Error',
                'Please enter a valid 10-digit mobile number starting with 6-9.',
                'error'
            );
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            return Swal.fire('Error', 'Please enter a valid email address.', 'error');
        }
        if (!formData.aadharNumber.match(/^[0-9]{12}$/)) return Swal.fire('Error', 'Please enter a valid 12-digit Aadhar number.', 'error');
        if (formData.optionalSubjects.length !== 3) return Swal.fire('Error', 'Please select exactly 3 optional subjects.', 'warning');
        if (userCaptcha !== captcha) return Swal.fire('Error', 'Incorrect Captcha Code.', 'error');
        if (!files.photo) return Swal.fire('Error', 'Passport photo is required.', 'error');
        if (!formData.declaration) return Swal.fire('Error', 'Please accept the declaration.', 'warning');
        if (Number(formData.obtainedMarks) > Number(formData.totalMarks)) {
            return Swal.fire('Error', 'Obtained marks cannot exceed total marks.', 'error');
        }

        if (!/^[0-9]{2}$/.test(formData.sessionStart) ||
            !/^[0-9]{2}$/.test(formData.sessionEnd)) {
            return Swal.fire(
                'Error',
                'Please enter valid 2-digit session years.',
                'error'
            );
        }

        setIsLoading(true);

        const data = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'optionalSubjects') data.append(key, JSON.stringify(formData[key]));
            else data.append(key, formData[key]);
        });
        // Append only existing files
        Object.keys(files).forEach(key => {
            if (files[key]) data.append(key, files[key]);
        });

        try {
            // Replace with your actual API endpoint
            const response = await axios.post(`${API_BASE_URL}/api/admissions/submit`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.status === 201 || response.status === 200) {
                Swal.fire({
                    title: 'Application Submitted!',
                    text: 'Form submitted successfully.',
                    icon: 'success'
                }).then(() => window.location.reload());
            }
        } catch (error) {
            console.error("Submission failed:", error);
            Swal.fire('Submission Failed', error.response?.data?.message || 'An error occurred.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
        <Helmet>
            <title>Online Admission Form | SK College</title>

            <meta
                name="description"
                content="Apply online for admission at S.K. College (Affiliated to JNVU, Jodhpur). Fill the official admission application form for Arts courses for the current academic session."
            />

            <meta
                name="keywords"
                content="SK College Admission, Online Admission Form, BA Admission Jodhpur, SK College JNVU, College Admission Rajasthan"
            />
            
            <meta property="og:title" content="Apply Online - SK College Admission" />
            <meta property="og:description" content="Fill the official online admission form for SK College (JNVU Affiliated), Jodhpur." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://skcollegemanaklao.com/admission" />

            <link rel="canonical" href="https://skcollegemanaklao.com/admission" />
        </Helmet>
        <div className="admission-form-wrapper">
            <div className="admission-form-container">
                
                {/* --- CUSTOM HEADER WITH LOGO --- */}
                <div className="sk-college-header">
                    <div className="header-layout-flex">
                        {/* Logo Section */}
                        <div className="header-logo-container">
                            <img src={logo} alt="S.K. College Logo" className="college-logo-img" />
                        </div>
                        
                        {/* Text Section */}
                        <div className="header-text-container">
                            <h1 className="hindi-title">सुचेता कृपलानी महाविद्यालय</h1>
                            <h2 className="eng-title">S.K. College</h2>
                            <p className="affiliation">जय नारायण व्यास विश्वविद्यालय, जोधपुर (JNVU), राजस्थान से सम्बद्ध (Affiliated) है।</p>
                            <p className="address">पता :- ग्रा. पो. – मानकलाव, जोधपुर, 342305 राज.</p>
                        </div>
                    </div>

                    <div className="header-divider"></div>
                    
                    <div className="form-title-row">
                        <h3>प्रवेश आवेदन पत्र <span className="eng-sub">(Admission Form)</span></h3>
                    </div>
                    <div className="session-container">
                        <span>सत्र 20</span>
                        <input type="text" name="sessionStart" maxLength="2" value={formData.sessionStart} onChange={handleChange} className="session-input" placeholder="25" required/>
                        <span>- 20</span>
                        <input type="text" name="sessionEnd" maxLength="2" value={formData.sessionEnd} onChange={handleChange} className="session-input" placeholder="26" required/>
                    </div>
                </div>

                <form className="admission-form-content" onSubmit={handleSubmit}>
                    
                    {/* --- 1. STUDENT DETAILS --- */}
                    <div className="admission-form-section">
                        <h3 className="admission-form-section-title"><FaUser /> विद्यार्थी विवरण (Student Details)</h3>
                        
                        <div className="student-profile-layout">
                            {/* Left Side: Photo */}
                            <div className="profile-photo-area">
                                <div className="photo-upload-box" onClick={() => fileRefs.photo.current.click()}>
                                    {previews.photo ? (
                                        <img src={previews.photo} alt="Student" />
                                    ) : (
                                        <div className="placeholder">
                                            <FaUpload />
                                            <span>Upload Photo</span>
                                        </div>
                                    )}
                                </div>
                                <input type="file" ref={fileRefs.photo} onChange={(e) => handleFileChange(e, 'photo')} accept="image/*" hidden />
                                <small>Passport Size (50KB - 200KB)</small>
                            </div>

                            {/* Right Side: Inputs */}
                            <div className="profile-inputs-area">
                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label>विद्यार्थी का नाम (अंग्रेजी में) <span className="req">*</span></label>
                                        <input type="text" name="studentName" value={formData.studentName} onChange={handleChange}  pattern="[A-Za-z\s]+" placeholder="Name in English" required />
                                    </div>
                                    <div className="form-group">
                                        <label>जन्म तिथि / DOB <span className="req">*</span></label>
                                        <input type="date" name="dob" value={formData.dob} onChange={handleChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>लिंग / Gender <span className="req">*</span></label>
                                        <select name="gender" value={formData.gender} onChange={handleChange} required>
                                            <option value="">Select</option>
                                            <option value="Male">पुरुष / Male</option>
                                            <option value="Female">महिला / Female</option>
                                            <option value="Other">अन्य / Other</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>रक्त समूह / Blood Group</label>
                                        <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                                            <option value="">Select (Optional)</option>
                                            <option value="A+">A+</option><option value="A-">A-</option>
                                            <option value="B+">B+</option><option value="B-">B-</option>
                                            <option value="O+">O+</option><option value="O-">O-</option>
                                            <option value="AB+">AB+</option><option value="AB-">AB-</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>आधार क्रमांक / Aadhar Number <span className="req">*</span></label>
                                        <input type="text" name="aadharNumber" value={formData.aadharNumber} onChange={handleChange} maxLength="12" placeholder="12 Digit Number" pattern="[0-9]{12}" inputMode="numeric" required />
                                    </div>
                                    <div className="form-group">
                                        <label>श्रेणी (Category) <span className="req">*</span></label>
                                        <select name="category" value={formData.category} onChange={handleChange} required>
                                            <option value="">Select</option>
                                            <option value="General">सामान्य (General)</option>
                                            <option value="OBC">अन्य पिछड़ा वर्ग (OBC)</option>
                                            <option value="SC">अनुसूचित जाति (SC)</option>
                                            <option value="ST">अनुसूचित जनजाति (ST)</option>
                                            <option value="EWS">आर्थिक रूप से कमजोर वर्ग (EWS)</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label>पिता का नाम / Father's Name <span className="req">*</span></label>
                                        <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>माता का नाम / Mother's Name <span className="req">*</span></label>
                                        <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>जाति / Caste <span className="req">*</span></label>
                                        <input type="text" name="caste" value={formData.caste} onChange={handleChange} required />
                                    </div>
                                    
                                    {/* Religion Dropdown Implementation */}
                                    <div className="form-group">
                                        <label>धर्म / Religion <span className="req">*</span></label>
                                        <select 
                                            name="religionSelect" 
                                            onChange={handleReligionDropdownChange} 
                                            value={isOtherReligion ? "Other" : formData.religion}
                                            required
                                        >
                                            <option value="">Select</option>
                                            <option value="Hindu">Hindu</option>
                                            <option value="Muslim">Muslim</option>
                                            <option value="Sikh">Sikh</option>
                                            <option value="Christian">Christian</option>
                                            <option value="Jain">Jain</option>
                                            <option value="Buddhist">Buddhist</option>
                                            <option value="Parsi">Parsi</option>
                                            <option value="Jewish">Jewish</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {isOtherReligion && (
                                            <input 
                                                type="text" 
                                                name="religion" 
                                                value={formData.religion} 
                                                onChange={handleChange} 
                                                placeholder="Specify Religion"
                                                style={{ marginTop: '8px' }}
                                                required 
                                            />
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- 2. CONTACT DETAILS --- */}
                    <div className="admission-form-section">
                        <h3 className="admission-form-section-title"><FaMapMarkerAlt /> संपर्क जानकारी (Contact Details)</h3>
                        <div className="form-grid-1">
                            <div className="form-group">
                                <label>1. स्थायी पता / Permanent Address <span className="req">*</span></label>
                                <textarea name="permanentAddress" value={formData.permanentAddress} onChange={handleChange} onBlur={handleAddressBlur} placeholder="पूरा पता लिखें।" rows="2" required></textarea>
                            </div>
                            
                            <div className="checkbox-group highlight-checkbox">
                                <input type="checkbox" name="sameAddress" id="sameAdd" checked={formData.sameAddress} onChange={handleChange} />
                                <label htmlFor="sameAdd">अगर समान है तो (Same as Permanent Address)</label>
                            </div>

                            <div className="form-group">
                                <label>2. वर्तमान पता / Current Address <span className="req">*</span></label>
                                <textarea name="currentAddress" value={formData.currentAddress} onChange={handleChange} placeholder="यदि अलग हो तो लिखें।" rows="2" required readOnly={formData.sameAddress}></textarea>
                            </div>
                        </div>
                        <div className="form-grid-2 mt-3">
                            <div className="form-group">
                                <label>3. मोबाइल नंबर / Mobile Number <span className="req">*</span></label>
                                <div className="prefix-input">
                                    <span>+91</span>
                                    <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} maxLength="10" placeholder="9876543210" pattern="[6-9][0-9]{9}" inputMode="numeric" required />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>4. ईमेल आईडी / Email ID <span className="req">*</span></label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="student@example.com" required />
                            </div>
                        </div>
                    </div>

                    {/* --- 3. EDUCATIONAL QUALIFICATION --- */}
                    <div className="admission-form-section">
                        <h3 className="admission-form-section-title"><FaGraduationCap /> शैक्षणिक योग्यता (Educational Qualification)</h3>
                        <div className="form-grid-2">
                            <div className="form-group">
                                <label>Class for Admission / कक्षा जिसमें प्रवेश लेना चाहते हैं <span className="req">*</span></label>
                                <input type="text" name="admissionClass" value={formData.admissionClass} onChange={handleChange} placeholder="e.g. B.A. First Year" required />
                            </div>
                            <div className="form-group">
                                <label>Last Passed Exam / अंतिम उत्तीर्ण परीक्षा का नाम <span className="req">*</span></label>
                                <input type="text" name="lastExamName" value={formData.lastExamName} onChange={handleChange} placeholder="e.g. 12th Arts" required />
                            </div>
                            <div className="form-group">
                                <label>Status in Previous Exam / गत परीक्षा में आप <span className="req">*</span></label>
                                <div className="radio-group">
                                    <label><input type="radio" name="studentStatus" value="Regular" checked={formData.studentStatus === 'Regular'} onChange={handleChange} /> नियमित (Regular)</label>
                                    <label><input type="radio" name="studentStatus" value="Private" checked={formData.studentStatus === 'Private'} onChange={handleChange} /> स्वयंपाठी (Private)</label>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Last Institution Attended / अंतिम संस्था जहाँ अध्ययन किया <span className="req">*</span></label>
                                <input type="text" name="lastInstitution" value={formData.lastInstitution} onChange={handleChange} placeholder="School/College Name" required />
                            </div>
                        </div>
                    </div>

                    {/* --- 4. RESULT DETAILS --- */}
                    <div className="admission-form-section">
                        <h3 className="admission-form-section-title"><FaCalculator /> अंतिम परीक्षा परिणाम का विवरण (Result Details)</h3>
                        <div className="form-grid-3">
                            <div className="form-group">
                                <label>पूर्णांक / Total Marks <span className="req">*</span></label>
                                <input type="number" name="totalMarks" value={formData.totalMarks} onChange={handleChange} placeholder="e.g. 600" min="0"  required />
                            </div>
                            <div className="form-group">
                                <label>प्राप्तांक / Marks Obtained <span className="req">*</span></label>
                                <input type="number" name="obtainedMarks" value={formData.obtainedMarks} onChange={handleChange} placeholder="e.g. 450" min="0"required />
                            </div>
                            <div className="form-group">
                                <label>प्रतिशत / Percentage (%)</label>
                                <input type="text" name="percentage" value={formData.percentage} readOnly className="readonly-input" placeholder="Auto Calculated" />
                            </div>
                        </div>
                    </div>

                    {/* --- 5. SUBJECT SELECTION --- */}
                    <div className="admission-form-section">
                        <h3 className="admission-form-section-title"><FaBookOpen /> विषय चयन (Subject Selection)</h3>
                        
                        <div className="form-grid-2 subject-settings">
                            <div className="form-group box-style">
                                <label>अनिवार्य विषय (Compulsory Subject) <span className="req">*</span></label>
                                <div className="radio-group-vertical">
                                    <label><input type="radio" name="compulsorySubject" value="Hindi" checked={formData.compulsorySubject === 'Hindi'} onChange={handleChange} required /> हिंदी (Hindi)</label>
                                    <label><input type="radio" name="compulsorySubject" value="English" checked={formData.compulsorySubject === 'English'} onChange={handleChange} /> अंग्रेजी (English)</label>
                                </div>
                            </div>
                            <div className="form-group box-style">
                                <label>माध्यम (Medium) <span className="req">*</span></label>
                                <div className="radio-group-vertical">
                                    <label><input type="radio" name="medium" value="Hindi" checked={formData.medium === 'Hindi'} onChange={handleChange} required /> हिंदी (Hindi)</label>
                                    <label><input type="radio" name="medium" value="English" checked={formData.medium === 'English'} onChange={handleChange} /> अंग्रेजी (English)</label>
                                </div>
                            </div>
                        </div>

                        <div className="optional-subjects-container">
                            <h4>ऐच्छिक विषय (Optional Subjects) - <small>इनमें से कोई तीन चुनें (Select any 3)</small></h4>
                            <div className="checkbox-grid">
                                {OPTIONAL_SUBJECTS.map((sub, idx) => {
                                    const isSelected = formData.optionalSubjects.includes(sub);
                                    const isDisabled =
                                        formData.optionalSubjects.length >= 3 && !isSelected;

                                    return (
                                        <label
                                            key={idx}
                                            className={`subject-item ${isSelected ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                name="optionalSubjects"
                                                value={sub}
                                                checked={isSelected}
                                                disabled={isDisabled}
                                                onChange={handleChange}
                                            />
                                            {sub}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* --- FOOTER --- */}
                    <div className="admission-form-final-section">
                        
                        {/* Improved Captcha */}
                        <div className="captcha-container">
                            <label>Security Check / सुरक्षा जाँच <span className="req">*</span></label>
                            <div className="captcha-box">
                                <span className="captcha-code">{captcha}</span>
                                <button type="button" className="refresh-btn" onClick={generateCaptcha} title="Refresh Code">
                                    <FaSyncAlt />
                                </button>
                            </div>
                            <input 
                                type="text" 
                                className="captcha-input"
                                placeholder="Enter the code above" 
                                value={userCaptcha} 
                                onChange={(e) => setUserCaptcha(e.target.value)} 
                                required
                            />
                        </div>

                        <div className="admission-form-declaration">
                            <label>
                                <input type="checkbox" name="declaration" checked={formData.declaration} onChange={handleChange} />
                                <span>
                                    मेरे द्वारा दी गई जानकारी सत्य है। यदि कोई जानकारी गलत पाई जाती है तो प्रवेश रद्द किया जा सकता है।
                                    <br/>
                                    <small>(I hereby declare that the information provided above is true. If found false, admission may be cancelled.)</small>
                                </span>
                            </label>
                        </div>

                        <button type="submit" className="admission-form-submit-btn" disabled={isLoading}>
                            {isLoading ? 'Submitting...' : 'Submit Application'} <FaPaperPlane />
                        </button>
                    </div>

                </form>
            </div>
        </div>
        </>
    );
};

export default AdmissionForm;